/* eslint-disable consistent-return */
import Cookies from 'js-cookie';
import {
  isLocalDev,
  isImagitasDev,
  isImagitasTest,
  isImagitasPreprod,
} from '@mgo/core/src/environment';


const isPostalEnv = () => {
  const origin = window.location.href;
  switch (true) {
    case isLocalDev(origin):
    case isImagitasDev(origin):
    case isImagitasTest(origin):
    case isImagitasPreprod(origin):
      return false;

    default:
      return true;
  }
};

/**
 * Ampersend Experiences Client Methods
 * https://github.com/RedVentures/ampersend-client
 */
export default {
  install(Vue, { key, persist = false, logLevel = 'info' }) {
    /* Ampersend client and UI/SDK are loaded via CDN
    so that they can be updated outside of MGO's release cycle. */
    const { Ampersend } = window.Ampersend;
    let ampData = [];
    const previous = JSON.parse(sessionStorage.getItem('amp_data')) ?? [];

    if (!isPostalEnv() && !window.AmpersendUI?.default) {
      const s = document.createElement('script');
      s.type = 'application/javascript';
      s.src = 'https://cdn.ampersend.io/ampersend-client/stable/ui.js';

      const l = document.createElement('link');
      l.rel = 'stylesheet';
      l.href = 'https://cdn.ampersend.io/ampersend-client/stable/ui.css';

      document.head.appendChild(l);
      document.body.appendChild(s);
    }

    // https://github.com/RedVentures/ampersend-client
    const client = new Ampersend(
      'https://api.ampersend.io',
      key,
      {
        persist,
        logLevel,
      },
    );

    const STORAGE_ANONYMOUS_ID = 'ampersend-user-id';
    const anonymousId = Cookies.get(STORAGE_ANONYMOUS_ID);

    Object.assign(Vue.prototype, {
      $amp: {
        sdkInstantiated: false,
        isPostalEnv: isPostalEnv(),
        /**
         * apply anonymous id to experience requests
         * @param {String} id - typically store.offers.stitchId
         */
        setAnonymousID(id) {
          Cookies.set(STORAGE_ANONYMOUS_ID, id, { secure: !isLocalDev(window.location.origin) });
          client.setAnonymousID(id);
        },

        /**
         * patch user identity
         * @param {Object} properties - key,value pairs of ampersend properties
         */
        async identify(properties) {
          await client.identify({
            anonymousID: Cookies.get(STORAGE_ANONYMOUS_ID),
            properties,
          });
        },

        /**
         * get All experiences
         * @param {Array.<number>} accountIds[] - Ampersend account ids
         * @return {Promise}
         */
        async getLiveExperiences(accountIds = [355]) {
          try {
            if (!Cookies.get(STORAGE_ANONYMOUS_ID)) {
              return ampData;
            }
            const { experiences } = await client.getLiveExperiences(
              accountIds,
              Cookies.get(STORAGE_ANONYMOUS_ID),
            );
            if (!experiences?.length) {
              window.localStorage.setItem('ampersend_data', encodeURI(JSON.stringify(ampData)));
              return ampData;
            }
            ampData = [...previous, ...experiences];

            const expInfo = experiences.map(({ templates, ...e }) => e);
            window.localStorage.setItem('ampersend_data', encodeURI(JSON.stringify(expInfo)));
            window.sessionStorage.setItem('amp_data', JSON.stringify(ampData));
            return experiences;
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
          }

          return ampData;
        },
        /**
         * @since v4.6.1 autoimpress is disabled for this event to enable tracking in amp.
         * @param {Array<number>} accountIds
         * @returns {Array<Object>}
         */

        async getUnimpressedExperiences(accountIds = [355]) {
          try {
            const { experiences } = await client.getLiveExperiences(
              accountIds,
              Cookies.get(STORAGE_ANONYMOUS_ID),
              // { autoImpress: false },
            );
            ampData = [...previous, ...experiences];
            window.sessionStorage.setItem('amp_data', JSON.stringify(ampData));

            return experiences;
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
          }

          return [];
        },

        /**
         * Reward an experience to evolve the DAPI Model
         * @param {Number} agentID - the id of the experience
         */
        reward(agentId) {
          client.trackExperienceDecisionCreated(agentId);
        },

        /**
         * Veil target element until either a valid experience returns or Ampersend errors
         * Typically called on page load
         * @param {HTMLElement} element - element to veil
         */
        veil(element) {
          return client.veil(element);
        },

        /**
         * Unveil a previously veiled target element
         * Typically called after getLiveExperiences is called
         * @param {HTMLElement} element - element to veil
         */
        unveil(element) {
          return client.unveil(element);
        },

        /**
         * attaches success criteria that was not available at the time of
         * calling getLiveExperiences
         * @return {Promise}
         */
        attachSuccessCriteria() {
          return client.attachSuccessCriteria();
        },

        /**
         * initializes AmpersendUI SDK for testing of experiences
         * https://github.com/RedVentures/ampersend-client#ampersend-ui
         */
        initSDK() {
          const AmpersendUI = window.AmpersendUI?.default;

          if (this.isPostalEnv || this.sdkInstantiated || !AmpersendUI) return;

          this.sdkInstantiated = true;

          return new AmpersendUI('https://api.ampersend.io', key);
        },
      },
    });

    if (anonymousId) {
      client.setAnonymousID(anonymousId);
    }
  },
};
